/**
 * SegmentsEditor Component
 *
 * Entity-specific editor for Segments lookup table
 * Two fields: segment_name, program_name
 */

import {
  useSegments,
  useCreateSegment,
  useUpdateSegment,
  useDeleteSegment,
} from '@medsuite/api-client';

import EntityEditor from '../components/EntityEditor';

import type { Segment } from '@medsuite/models';

interface SegmentsEditorProps {
  onDataLoaded?: (count: number) => void;
}

export default function SegmentsEditor({ onDataLoaded }: SegmentsEditorProps) {
  const { data: segments = [], isLoading, error } = useSegments();

  // Mutations
  const createMutation = useCreateSegment();
  const updateMutation = useUpdateSegment();
  const deleteMutation = useDeleteSegment();

  const columns = [
    { key: 'segment_id', label: 'ID', readonly: true },
    { key: 'segment_name', label: 'Segment Name', type: 'text' as const },
    { key: 'program_name', label: 'Program Name', type: 'text' as const },
  ];

  // Handle add
  const handleAdd = async (item: Partial<Record<string, unknown>>) => {
    const seg = item as Partial<Segment>;
    if (!seg.segment_name || !seg.program_name) {
      throw new Error('Segment name and program name are required');
    }
    await createMutation.mutateAsync({
      segment_name: seg.segment_name,
      program_name: seg.program_name,
    });
  };

  // Handle update
  const handleUpdate = async (id: string | number, item: Partial<Record<string, unknown>>) => {
    const seg = item as Partial<Segment>;
    await updateMutation.mutateAsync({
      id: Number(id),
      data: {
        segment_name: seg.segment_name,
        program_name: seg.program_name,
      },
    });
  };

  // Handle delete
  const handleDelete = async (id: string | number) => {
    await deleteMutation.mutateAsync(Number(id));
  };

  return (
    <EntityEditor
      title="Segments"
      columns={columns}
      data={segments as unknown as Record<string, unknown>[]}
      isLoading={isLoading}
      error={error}
      primaryKey="segment_id"
      onDataLoaded={onDataLoaded}
      onAdd={handleAdd}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      isAdding={createMutation.isPending}
      isUpdating={updateMutation.isPending}
      isDeleting={deleteMutation.isPending}
    />
  );
}
